import { useState, useCallback } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
    TourDemoType,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
    demo?: TourDemoType
}

const TOUR_STEPS: TourStep[] = [
    // Action buttons - chi tiết từng nút
    {
        selector: '[data-tour="action-non-invoicable"]',
        title: 'Non-Invoicable',
        description:
            'Duyệt hóa đơn không xuất VAT:\n• Dùng cho hóa đơn nội bộ\n• Không cần xuất hóa đơn đỏ\n• Chỉ active khi trạng thái "Chờ xác nhận"',
    },
    {
        selector: '[data-tour="action-approve"]',
        title: 'Duyệt hóa đơn (Invoicable)',
        description:
            'Duyệt hóa đơn có thể xuất VAT:\n• Sẽ được xuất hóa đơn đỏ\n• Chuyển sang trạng thái "Đã duyệt"\n• Chỉ active khi trạng thái "Chờ xác nhận"',
    },
    {
        selector: '[data-tour="action-pay"]',
        title: 'Thanh toán hóa đơn',
        description:
            'Ghi nhận thanh toán:\n• Chọn phương thức (CK/TM/Ví...)\n• Chọn ngày thanh toán\n• Chỉ active khi trạng thái "Đã duyệt"',
        demo: 'pay-modal',
    },
    {
        selector: '[data-tour="action-cancel"]',
        title: 'Hủy hóa đơn',
        description:
            'Hủy hóa đơn chưa thanh toán:\n• Chọn lý do hủy từ danh sách\n• Nhập ghi chú chi tiết\n• Hóa đơn chuyển sang "Đã hủy"',
        demo: 'delete-modal',
    },
    {
        selector: '[data-tour="action-delete"]',
        title: 'Xóa hóa đơn',
        description:
            'Xóa vĩnh viễn hóa đơn:\n• Chọn lý do xóa từ danh sách\n• Nhập ghi chú chi tiết\n• Chỉ xóa được hóa đơn "Chờ xác nhận"',
        demo: 'delete-modal',
    },
    {
        selector: '[data-tour="action-preview-mail"]',
        title: 'Xem trước mail',
        description:
            'Xem trước nội dung email Statement:\n• Kiểm tra thông tin trước khi gửi\n• Xem chi tiết các hạng mục\n• Tải PDF nếu cần',
        demo: 'preview-mail',
    },
    {
        selector: '[data-tour="action-send-mail"]',
        title: 'Gửi mail',
        description:
            'Gửi email thông báo cho khách hàng:\n• Nhập người nhận, CC, BCC\n• Email lấy từ thông tin khách hàng\n• Theo dõi lịch sử gửi ở dưới',
        demo: 'send-mail',
    },
    {
        selector: '[data-tour="action-add-update"]',
        title: 'Thêm cập nhật',
        description:
            'Ghi chú thay đổi trên hóa đơn:\n• Chọn loại cập nhật\n• Nhập mô tả chi tiết\n• Lưu lịch sử để theo dõi',
        demo: 'add-update',
    },
    {
        selector: '[data-tour="action-revenue-allocation"]',
        title: 'Xem phân bổ doanh thu',
        description:
            'Preview phân bổ doanh thu:\n• Xem chi tiết phân bổ theo tháng\n• Dựa trên thời gian dịch vụ\n• Dùng cho báo cáo tài chính',
    },
    {
        selector: '[data-tour="action-rollback"]',
        title: 'Rollback hóa đơn',
        description:
            'Hoàn tác hóa đơn đã thanh toán:\n• Chuyển về trạng thái "Đã duyệt"\n• Xóa thông tin thanh toán\n• Chỉ dùng khi có lỗi',
    },
    // Invoice info
    {
        selector: '[data-tour="invoice-info"]',
        title: 'Thông tin hóa đơn',
        description:
            'Thông tin cơ bản:\n• Khách hàng (click để xem chi tiết)\n• Ngày tạo, hạn thanh toán\n• Tổng tiền, trạng thái\n• Số tiền đã thanh toán (nếu có)',
    },
    // Commission & KPI - Tổng quan
    {
        selector: '[data-tour="invoice-commission"]',
        title: 'Hoa hồng & KPI',
        description:
            'Thông tin tính hoa hồng:\n• Nhân viên hưởng hoa hồng\n• Loại bán hàng (Mới/Gia hạn/Upsale)\n• Tỷ lệ KPI áp dụng cho loại này',
    },
    // Commission - Nhân viên
    {
        selector: '[data-tour="commission-employee"]',
        title: 'Nhân viên hưởng hoa hồng',
        description:
            'Thông tin nhân viên:\n• Tên nhân viên được gán cho HĐ\n• Mã nhân viên (nếu có)\n• Hoa hồng sẽ tính cho NV này\n• Có thể có nhiều NV cùng hưởng',
    },
    // Commission - Loại bán hàng
    {
        selector: '[data-tour="commission-sale-type"]',
        title: 'Loại bán hàng',
        description:
            'Phân loại hóa đơn:\n• Bán mới (New Sale) - KH mới\n• Gia hạn (Renewal) - Tái ký\n• Upsale - Nâng cấp dịch vụ\n• Ảnh hưởng tỷ lệ KPI áp dụng',
    },
    // Commission - Tỷ lệ KPI
    {
        selector: '[data-tour="commission-kpi-rate"]',
        title: 'Tỷ lệ KPI áp dụng',
        description:
            'Tỷ lệ tính hoa hồng:\n• Dựa trên loại bán hàng\n• Bán mới: thường cao nhất\n• Gia hạn: thấp hơn bán mới\n• Upsale: tùy theo chính sách',
    },
    // Thêm dòng hóa đơn
    {
        selector: '[data-tour="action-add-invoice-line"]',
        title: 'Thêm dòng hóa đơn',
        description:
            'Thêm hạng mục vào hóa đơn:\n• Chọn dịch vụ từ danh sách\n• Nhập tên chi tiết, mô tả\n• Điền thông số, đơn giá\n• Tính thuế nếu cần',
        demo: 'add-invoice-line',
    },
    // Invoice items
    {
        selector: '[data-tour="invoice-items"]',
        title: 'Danh sách hạng mục',
        description:
            'Chi tiết các dịch vụ trong hóa đơn:\n• Tên dịch vụ, mã dịch vụ\n• Đơn giá, số lượng, VAT\n• Thời gian áp dụng\n• Tổng tiền từng hạng mục',
    },
    // Invoice updates
    {
        selector: '[data-tour="invoice-updates"]',
        title: 'Lịch sử cập nhật',
        description:
            'Theo dõi các thay đổi:\n• Ai thực hiện thay đổi\n• Thời gian thay đổi\n• Nội dung thay đổi\n• Loại cập nhật',
    },
    // Transactions
    {
        selector: '[data-tour="invoice-transactions"]',
        title: 'Giao dịch thanh toán',
        description:
            'Danh sách các giao dịch:\n• Số tiền, phí giao dịch\n• Phương thức thanh toán\n• Trạng thái giao dịch\n• Ghi chú',
    },
    // Mail history
    {
        selector: '[data-tour="invoice-mail-history"]',
        title: 'Lịch sử gửi mail',
        description:
            'Theo dõi email đã gửi:\n• Người nhận, CC, BCC\n• Thời gian gửi\n• Trạng thái gửi\n• Nội dung email',
    },
]

export const useInvoiceDetailTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const getVisibleSteps = useCallback(() => {
        return TOUR_STEPS.filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                if (stepIndex < steps.length - 1) {
                    showTourStep(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => goToStep(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                    demo: step.demo,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour]
    )

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            setCurrentStep(stepIndex)
            showTourStep(stepIndex, steps)
        },
        [showTourStep]
    )

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible elements to tour')
            return
        }

        setIsActive(true)
        setCurrentStep(0)
        showTourStep(0, visibleSteps)
    }, [getVisibleSteps, showTourStep])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}

export default useInvoiceDetailTour
